'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  CaretDownIcon,
  FilesIcon,
  FolderOpenIcon,
} from '@phosphor-icons/react';
// Typechecks once @tranquility-ai/theme-components 3.9.0 is pinned.
import { Checkbox } from '@tranquility-ai/theme-components';
import { Button } from '@tranquility-ai/theme-components/button';
import { Chip, ChipGroup } from '@tranquility-ai/theme-components/chip';
import { fileTypeControllers } from '@/shared/constants';
import { fileTypeIcons } from '@/shared/uiConstants';
import {
  fileInfoFromProvenanceBody,
  resolveFileInfoMediaDisplayUrl,
} from '@/shared/functions';
import type {
  FileSummary,
  ProvenanceFileInfoBody,
  TreeNodeResponse,
} from '@/shared/cors-provenance-api';
import type { Source } from '@/shared/types';
import { useTimePilotT, type TranslateFn } from '@/i18n/useTimePilotT';

export function fileSummaryFromTreeNode(node: TreeNodeResponse): FileSummary | null {
  const fi = node.fileInfo;
  if (!fi) return null;
  return fileInfoFromProvenanceBody(fi as ProvenanceFileInfoBody);
}

/** Build a SourceCard citation that preserves provenance ``fileInfo`` (incl. CM description). */
export function buildEvidenceLockerPreviewSource(
  lookupUrl: string,
  fileInfo: FileSummary | null | undefined,
  pageCount?: number | null,
): Source {
  const displayUrl = resolveFileInfoMediaDisplayUrl(fileInfo, lookupUrl);
  const previewFromFileInfo =
    typeof fileInfo?.previewUrl === 'string' ? fileInfo.previewUrl.trim() : '';
  const displayIsProvenancePreview =
    !!previewFromFileInfo && displayUrl === previewFromFileInfo;
  const usesAlternateDisplay = displayUrl !== lookupUrl;
  const searchablePath =
    typeof fileInfo?.searchablePath === 'string' ? fileInfo.searchablePath.trim() : '';
  return {
    sourceUrl: lookupUrl,
    sourceFilePath: lookupUrl,
    ...(searchablePath ? { searchablePath } : {}),
    pageCount: pageCount ?? undefined,
    fileInfo: fileInfo ?? undefined,
    mediaUrl: displayUrl,
    mediaSrc: displayUrl,
    srcToUse: displayUrl,
    // Do not stamp ``previewFilePath`` with ``fileInfo.previewUrl`` — that
    // makes downstream lookups treat the transcode as a citation extract and
    // re-fetch file-info keyed on the preview URL.
    ...(usesAlternateDisplay && !displayIsProvenancePreview
      ? { previewFilePath: displayUrl }
      : {}),
    ...(usesAlternateDisplay ? { mediaContext: { previewUrl: displayUrl } } : {}),
  } as Source;
}

/**
 * Original (``bypass_cm``) preview: keep the locker citation identity, but drop
 * playback URLs that were resolved from the CM-safe ``previewUrl``. Otherwise
 * SourceCard looks up file-info for the transcode even with ``bypassCm``.
 */
export function toBypassCmPreviewSource(source: Source): Source {
  return {
    ...source,
    previewFilePath: undefined,
    mediaUrl: undefined,
    mediaSrc: undefined,
    srcToUse: undefined,
    mediaContext: source.mediaContext
      ? { ...source.mediaContext, previewUrl: undefined }
      : undefined,
    fileInfo: source.fileInfo
      ? { ...source.fileInfo, previewUrl: null }
      : undefined,
    bypassCm: true,
  };
}

/** Chip row labels (PDF, …) → provenance fileTypes tokens. */
const EVIDENCE_LISTING_TYPE_TOKEN_BY_UI_KEY: Record<string, string> = {
  PDF: 'pdf',
  'MS Office': 'office',
  Image: 'image',
  Video: 'video',
  Audio: 'audio',
  Spreadsheet: 'spreadsheet',
  Email: 'email',
  Other: 'other',
};

export function evidenceFileTypeLabel(type: string, t: TranslateFn): string {
  const token = EVIDENCE_LISTING_TYPE_TOKEN_BY_UI_KEY[type];
  return token ? t(`chat.evidence.fileType.${token}`) : type;
}

export function fileTypesTokensForEvidenceListing(uiKeys: string[]): string[] {
  return uiKeys.map((k) => EVIDENCE_LISTING_TYPE_TOKEN_BY_UI_KEY[k] ?? k.toLowerCase());
}

/**
 * Inverse of {@link fileTypesTokensForEvidenceListing}: provenance/assistant
 * wire tokens (``pdf``, ``office``, …) → UI chip-row labels (``PDF``,
 * ``MS Office``, …). Used when re-hydrating the locker from chat-history
 * echoes, which only persist ``content_categories`` in wire form. Falls back
 * to the original token if no mapping is known so unrecognised values still
 * appear (instead of being silently dropped).
 */
const UI_KEY_BY_EVIDENCE_LISTING_TYPE_TOKEN: Record<string, string> = Object.fromEntries(
  Object.entries(EVIDENCE_LISTING_TYPE_TOKEN_BY_UI_KEY).map(([uiKey, token]) => [token, uiKey]),
);

export function uiKeysForEvidenceListingTokens(tokens: string[]): string[] {
  return tokens.map((t) => UI_KEY_BY_EVIDENCE_LISTING_TYPE_TOKEN[t.toLowerCase()] ?? t);
}

export type FileTypeFilterState = 'neutral' | 'included' | 'excluded';

/** Plus → check (include) → prohibit (exclude) → plus — mirrors locker row scope cycle. */
export function getNewActiveFileTypes(
  fileType: string,
  prev: Record<string, boolean>,
): Record<string, boolean> {
  const next = { ...prev };
  next[fileType] = !prev[fileType];
  return next;
}

export function getEvidenceCategoryScopeState(
  category: string,
  included: string[],
  excluded: string[],
): FileTypeFilterState {
  if (included.includes(category)) return 'included';
  if (excluded.includes(category)) return 'excluded';
  return 'neutral';
}

/** Plus → include → exclude → neutral — mirrors locker row/file scope cycle. */
export function cycleEvidenceCategoryScope(
  prev: {
    evidenceCategoryScopes?: string[];
    excludedEvidenceCategoryScopes?: string[];
  },
  category: string,
): {
  evidenceCategoryScopes: string[];
  excludedEvidenceCategoryScopes: string[];
} {
  const included = prev.evidenceCategoryScopes ?? [];
  const excluded = prev.excludedEvidenceCategoryScopes ?? [];

  if (included.includes(category)) {
    return {
      evidenceCategoryScopes: included.filter((c) => c !== category),
      excludedEvidenceCategoryScopes: [...excluded, category],
    };
  }
  if (excluded.includes(category)) {
    return {
      evidenceCategoryScopes: included,
      excludedEvidenceCategoryScopes: excluded.filter((c) => c !== category),
    };
  }
  return {
    evidenceCategoryScopes: [...included, category],
    excludedEvidenceCategoryScopes: excluded,
  };
}

export function evidenceAllFileTypesForCategoryScopes(
  includedScopeCats: string[],
  excludedScopeCats: string[],
  prevEvidenceAllFileTypes = false,
): boolean {
  if (includedScopeCats.length > 0 || excludedScopeCats.length > 0) {
    return false;
  }
  return !!prevEvidenceAllFileTypes;
}

/** Keep UI scope keys and provenance wire tokens in lockstep (no effect lag). */
export function evidenceCategoryScopeFieldsWithWireTokens(
  includedScopeCats: string[],
  excludedScopeCats: string[],
  prevEvidenceAllFileTypes = false,
): {
  evidenceCategoryScopes: string[];
  excludedEvidenceCategoryScopes: string[];
  contentCategories: string[];
  excludedContentCategories: string[];
  evidenceCategoryChips: string[];
  evidenceAllFileTypes: boolean;
} {
  return {
    evidenceCategoryScopes: includedScopeCats,
    excludedEvidenceCategoryScopes: excludedScopeCats,
    contentCategories:
      includedScopeCats.length > 0
        ? fileTypesTokensForEvidenceListing(includedScopeCats)
        : [],
    excludedContentCategories:
      excludedScopeCats.length > 0
        ? fileTypesTokensForEvidenceListing(excludedScopeCats)
        : [],
    evidenceCategoryChips: includedScopeCats,
    evidenceAllFileTypes: evidenceAllFileTypesForCategoryScopes(
      includedScopeCats,
      excludedScopeCats,
      prevEvidenceAllFileTypes,
    ),
  };
}

export function applyEvidenceCategoryScopeCycle(
  prev: {
    evidenceCategoryScopes?: string[];
    excludedEvidenceCategoryScopes?: string[];
    evidenceAllFileTypes?: boolean;
  },
  category: string,
) {
  const cycled = cycleEvidenceCategoryScope(prev, category);
  return evidenceCategoryScopeFieldsWithWireTokens(
    cycled.evidenceCategoryScopes,
    cycled.excludedEvidenceCategoryScopes,
    !!prev.evidenceAllFileTypes,
  );
}

/**
 * ScopeDropdown include checkbox: toggle membership in ``evidenceCategoryScopes``.
 * Checking a type also removes it from ``excludedEvidenceCategoryScopes`` so the
 * same label cannot appear in both include and exclude lists.
 */
export function toggleEvidenceCategoryScopeInclude(
  prev: {
    evidenceCategoryScopes?: string[];
    excludedEvidenceCategoryScopes?: string[];
    evidenceAllFileTypes?: boolean;
  },
  category: string,
) {
  const included = prev.evidenceCategoryScopes ?? [];
  const excluded = prev.excludedEvidenceCategoryScopes ?? [];
  const isIncluded = included.includes(category);
  const nextIncluded = isIncluded
    ? included.filter((c) => c !== category)
    : [...included, category];
  const nextExcluded = isIncluded
    ? excluded
    : excluded.filter((c) => c !== category);
  return evidenceCategoryScopeFieldsWithWireTokens(
    nextIncluded,
    nextExcluded,
    !!prev.evidenceAllFileTypes,
  );
}

/** Wire tokens for submit/API — always derived from UI exclude scope keys. */
export function excludedContentCategoriesFromScopes(
  excludedScopeCats: string[] | undefined | null,
): string[] {
  const scopes = excludedScopeCats ?? [];
  return scopes.length > 0 ? fileTypesTokensForEvidenceListing(scopes) : [];
}

/**
 * Merge category arrays from a history/answer echo with prior (optimistic) state.
 *
 * - ``undefined`` / omitted field → keep ``prev`` (server did not echo the field)
 * - defined array, including ``[]`` → trust the answer (explicit empty clears filters)
 */
export function categoryScopesFromAnswerOrPrev(
  answerVal: string[] | undefined,
  prevVal: string[] | undefined,
): string[] {
  if (answerVal === undefined) {
    return [...(prevVal ?? [])];
  }
  return [...answerVal];
}

export const FilterButton = ({
  isActive,
  onClick,
  icon: Icon,
  label,
  variant = 'compact',
  surface = 'light',
}: {
  isActive: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  icon: React.ElementType;
  label: string;
  variant?: 'compact' | 'expanded';
  surface?: 'light' | 'accent';
}) => (
  <Chip
    type="filter"
    layout="tile"
    surface={surface}
    size="sm"
    label={label}
    selected={isActive}
    leadingIcon={<Icon />}
    onToggle={() => {
      onClick({
        preventDefault: () => {},
        stopPropagation: () => {},
      } as React.MouseEvent<HTMLButtonElement>);
    }}
    className={variant === 'compact' ? 'min-w-[52px]' : 'min-w-[70px] sm:flex-1'}
  />
);

export type EvidenceLockerBrowseFilterChipsProps = {
  allFileTypes: boolean;
  activeFileTypes: Record<string, boolean>;
  onToggleAllFileTypes: () => void;
  onToggleFileType: (fileType: string) => void;
};

/** Horizontal pill chips for the evidence locker browse filter row (Figma). */
export function EvidenceLockerBrowseFilterChips({
  allFileTypes,
  activeFileTypes,
  onToggleAllFileTypes,
  onToggleFileType,
}: EvidenceLockerBrowseFilterChipsProps) {
  const t = useTimePilotT();
  const fileTypes = Object.keys(fileTypeControllers);

  return (
    <div className="filter-bar-scroll-desktop-hover min-w-0 touch-pan-x overflow-x-auto py-3 pl-4 pr-2">
      <ChipGroup className="w-max flex-nowrap pr-1">
        <Chip
          type="filter"
          surface="light"
          size="sm"
          label={t('chat.evidence.filters.allTypes')}
          selected={allFileTypes}
          onToggle={() => onToggleAllFileTypes()}
        />
        <span className="tp-chip-group-divider h-4" aria-hidden />
        {fileTypes.map((type) => {
          const isActive = !allFileTypes && activeFileTypes[type] === true;
          return (
            <Chip
              key={type}
              type="filter"
              surface="light"
              size="sm"
              label={evidenceFileTypeLabel(type, t)}
              selected={isActive}
              onToggle={() => onToggleFileType(type)}
            />
          );
        })}
      </ChipGroup>
    </div>
  );
}

export type ScopeByFileTypesChipGridProps = {
  selectedContentCategories: string[];
  excludedContentCategories?: string[];
  onToggleCategory: (category: string) => void;
  /** ``composer`` (blue) for chat picker; ``locker`` (neutral) for evidence locker side panel. */
  variant?: 'composer' | 'locker';
  /** Tighter chips for the narrow locker side panel. */
  density?: 'default' | 'compact';
  /** ``1`` = single vertical icon column (locker side panel). */
  columns?: 1 | 4;
};

/** Compact toggle grid for scope-by-file-type (e.g. chat filter picker). */
export function ScopeByFileTypesChipGrid({
  selectedContentCategories,
  excludedContentCategories = [],
  onToggleCategory,
  variant = 'composer',
  density = 'default',
  columns = 4,
}: ScopeByFileTypesChipGridProps) {
  const t = useTimePilotT();
  const isLocker = variant === 'locker';
  const isCompact = density === 'compact';
  const isSingleColumn = columns === 1;
  const allTypes = Object.keys(fileTypeControllers);
  return (
    <div
      className={`grid ${isSingleColumn ? 'grid-cols-1' : 'grid-cols-4'} ${isCompact ? 'gap-1' : 'gap-1.5'}`}
    >
      {allTypes.map((type) => {
        const Icon = fileTypeIcons[type as keyof typeof fileTypeIcons];
        const scopeState = getEvidenceCategoryScopeState(
          type,
          selectedContentCategories,
          excludedContentCategories,
        );
        const includedStyles = isLocker
          ? 'border-black/40 bg-black/10 text-black shadow-sm shadow-gray-300/30'
          : 'border-brand-blue-light/40 bg-brand-blue-light/20 text-brand-blue-light shadow-sm shadow-gray-300/30';
        const excludedStyles = isLocker
          ? 'border-red-600/50 bg-red-600/10 text-red-700 shadow-sm shadow-red-300/20'
          : 'border-red-600/50 bg-red-600/10 text-red-700 shadow-sm shadow-red-300/20';
        const neutralStyles = isLocker
          ? 'border-gray-200/30 bg-transparent text-black hover:border-black/20 hover:bg-black/10'
          : 'border-gray-200/30 bg-transparent text-gray-500 hover:border-brand-blue-light/20 hover:bg-brand-blue-light/10 hover:text-brand-blue-light';

        return (
          <button
            key={type}
            type="button"
            aria-label={
              scopeState === 'excluded'
                ? t('chat.evidence.category.excluded', { type: evidenceFileTypeLabel(type, t) })
                : scopeState === 'included'
                  ? t('chat.evidence.category.included', { type: evidenceFileTypeLabel(type, t) })
                  : t('chat.evidence.category.include', { type: evidenceFileTypeLabel(type, t) })
            }
            title={isSingleColumn ? type : undefined}
            aria-pressed={scopeState === 'included'}
            className={`box-border flex min-w-0 cursor-pointer items-center justify-center rounded-sm border-solid outline-none transition-colors duration-200 focus-visible:outline-none ${
              isSingleColumn
                ? 'aspect-square w-full border p-1'
                : isCompact
                  ? 'flex-col gap-0 border px-0.5 py-1'
                  : 'flex-col gap-0.5 border-[2px] px-0.5 py-1.5'
            } ${
              scopeState === 'included'
                ? includedStyles
                : scopeState === 'excluded'
                  ? excludedStyles
                  : neutralStyles
            }`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleCategory(type);
            }}
          >
            {Icon ? (
              <Icon
                size={isSingleColumn ? 18 : isCompact ? 16 : 22}
                weight="light"
                className={isLocker ? 'text-black' : undefined}
              />
            ) : null}
            {isSingleColumn ? null : (
              <span
                className={`max-w-full truncate px-0.5 text-center leading-tight tracking-tight ${
                  isCompact ? 'text-[8px]' : 'text-[9px]'
                }`}
              >
                {evidenceFileTypeLabel(type, t)}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export type ScopeDropdownProps = {
  selectedContentCategories: string[];
  onToggleCategory: (category: string) => void;
  /** ``panel`` shows the checkbox list inline; prefer ``ScopeByFileTypesChipGrid`` in tight layouts. */
  variant?: 'dropdown' | 'panel';
};

function ScopeByFileTypesList({
  selectedContentCategories,
  onToggleCategory,
  className,
}: {
  selectedContentCategories: string[];
  onToggleCategory: (category: string) => void;
  className?: string;
}) {
  const t = useTimePilotT();
  const allTypes = Object.keys(fileTypeControllers);
  return (
    <div className={className}>
      {allTypes.map((type) => {
        const Icon = fileTypeIcons[type as keyof typeof fileTypeIcons];
        return (
          <div
            key={type}
            className="flex cursor-pointer items-center gap-2 px-3 py-2 transition-colors duration-150 hover:bg-brand-blue-light/10"
            onClick={() => onToggleCategory(type)}
          >
            <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                className="min-w-[18px] max-w-[18px] min-h-[18px] max-h-[18px]"
                checked={selectedContentCategories.includes(type)}
                onCheckedChange={() => onToggleCategory(type)}
              />
            </span>
            {Icon && <Icon size={18} weight="light" className="text-black" />}
            <span className="text-[13px] text-black/80">{evidenceFileTypeLabel(type, t)}</span>
          </div>
        );
      })}
    </div>
  );
}

export function toggleEvidenceCategoryScope(
  prev: { evidenceCategoryScopes?: string[] },
  category: string,
): { evidenceCategoryScopes: string[] } {
  const current = prev.evidenceCategoryScopes ?? [];
  const next = current.includes(category)
    ? current.filter((c) => c !== category)
    : [...current, category];
  return { evidenceCategoryScopes: next };
}

export const ScopeDropdown = ({
  selectedContentCategories,
  onToggleCategory,
  variant = 'dropdown',
}: ScopeDropdownProps) => {
  const t = useTimePilotT();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedCount = selectedContentCategories.length;

  useEffect(() => {
    if (variant === 'panel') return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, variant]);

  if (variant === 'panel') {
    return (
      <ScopeByFileTypesList
        selectedContentCategories={selectedContentCategories}
        onToggleCategory={onToggleCategory}
        className="custom-scrollbar3 max-h-[280px] w-[184px] overflow-y-auto rounded-md border-[2px] border-solid border-brand-blue-light bg-white shadow-sm"
      />
    );
  }

  return (
    <div ref={dropdownRef} className="relative shrink-0">
      <Button
        type="button"
        style="outline"
        color="accent"
        background="default"
        size="sm"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className={`flex w-[184px] cursor-pointer items-center justify-between gap-2 rounded-lg border-[2px] border-solid border-brand-blue-light py-2 pl-4 pr-2 transition-colors duration-200 ${isOpen ? 'bg-brand-blue-light/20' : 'bg-transparent hover:bg-brand-blue-light/10'}`}
      >
        <span className="whitespace-nowrap text-[14px] tracking-[-0.42px] text-black">
          {selectedCount > 0
            ? t(selectedCount === 1
              ? 'chat.evidence.filters.fileTypeCount'
              : 'chat.evidence.filters.fileTypeCountPlural', { count: selectedCount })
            : t('chat.evidence.filters.scopeByFileTypes')}
        </span>
        <CaretDownIcon
          size={20}
          weight="regular"
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </Button>
      {isOpen ? (
        <ScopeByFileTypesList
          selectedContentCategories={selectedContentCategories}
          onToggleCategory={onToggleCategory}
          className="custom-scrollbar3 absolute right-0 top-full z-[60] mt-1 max-h-[240px] w-[184px] overflow-y-auto rounded-md border-[2px] border-solid border-brand-blue-light bg-white shadow-lg"
        />
      ) : null}
    </div>
  );
};

export interface EvidenceObject {
  mediaType: string;
  url: string;
  pageCount?: number | null;
  previewUrl?: string | null;
  contentModerationType?: string | null;
  /** Full provenance summary — carries ``contentModeration.imageDescription``. */
  fileInfo?: FileSummary | null;
}

export type EvidenceLockerFileTypeFilterBarProps = {
  showFolders: boolean;
  allFileTypes: boolean;
  activeFileTypes: Record<string, boolean>;
  selectedContentCategories?: string[];
  onToggleFolders: () => void;
  onToggleAllFileTypes: () => void;
  onToggleFileType: (fileType: string) => void;
  variant?: 'compact' | 'expanded';
};

export function EvidenceLockerFileTypeFilterBar({
  showFolders,
  allFileTypes,
  activeFileTypes,
  selectedContentCategories = [],
  onToggleFolders,
  onToggleAllFileTypes,
  onToggleFileType,
  variant = 'compact',
}: EvidenceLockerFileTypeFilterBarProps) {
  const t = useTimePilotT();
  return (
    <ChipGroup showDividers dividerVariant="tile" className="flex-nowrap">
      <FilterButton
        isActive={showFolders}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleFolders();
        }}
        icon={FolderOpenIcon}
        label={t('chat.evidence.filters.folders')}
        variant={variant}
      />
      <FilterButton
        isActive={allFileTypes}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleAllFileTypes();
        }}
        icon={FilesIcon}
        label={t('chat.evidence.filters.allFiles')}
        variant={variant}
      />
      {Object.keys(fileTypeControllers).map((key) => {
        const Icon = fileTypeIcons[key as keyof typeof fileTypeIcons];
        const isActive =
          activeFileTypes[key] ||
          (variant === 'compact' && selectedContentCategories.includes(key));
        return (
          <FilterButton
            key={key}
            isActive={!!isActive}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleFileType(key);
            }}
            icon={Icon}
            label={evidenceFileTypeLabel(key, t)}
            variant={variant}
          />
        );
      })}
    </ChipGroup>
  );
}
